import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { notesFraisAPI, projectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { ChevronLeft, Upload, X, FileText } from 'lucide-react';
import './NotesFrais.css';

const POSTES = [
  { value: 'FRAIS_DIVERS',       label: 'Frais divers' },
  { value: 'MISSION',            label: 'Mission' },
  { value: 'RECEPTION',          label: 'Réception' },
  { value: 'VOYAGE_DEPLACEMENT', label: 'Voyage & déplacement' },
];

const STATUT_META = {
  BROUILLON: { label: 'Brouillon', cls: 'nf-badge--brouillon' },
  SOUMIS:    { label: 'Soumis',    cls: 'nf-badge--soumis' },
  VALIDE:    { label: 'Validé',    cls: 'nf-badge--valide' },
  REFUSE:    { label: 'Refusé',    cls: 'nf-badge--refuse' },
  ANNULE:    { label: 'Annulé',    cls: 'nf-badge--annule' },
};

const EMPTY = {
  poste: 'FRAIS_DIVERS',
  designation: '',
  date_effet: '',
  montant_ttc: '',
  tva_5_5: '',
  tva_10: '',
  tva_20: '',
  project_id: '',
  commentaire: '',
};

export default function NotesFraisForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [form,     setForm]     = useState(EMPTY);
  const [note,     setNote]     = useState(null);
  const [projects, setProjects] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [loading,  setLoading]  = useState(isEdit);
  const [drag,     setDrag]     = useState(false);
  const [file,     setFile]     = useState(null);   // UploadFile (new)
  const [preview,  setPreview]  = useState(null);   // { url, type, name }
  const fileRef = useRef();

  useEffect(() => {
    projectsAPI.list({ statut: 'EN_COURS', for_me: true, limit: 500 })
      .then(r => setProjects(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    notesFraisAPI.get(id)
      .then(r => {
        const n = r.data;
        setNote(n);
        setForm({
          poste:       n.poste || 'FRAIS_DIVERS',
          designation: n.designation || '',
          date_effet:  n.date_effet || '',
          montant_ttc: n.montant_ttc?.toString() || '',
          tva_5_5:     n.tva_5_5 ? n.tva_5_5.toString() : '',
          tva_10:      n.tva_10  ? n.tva_10.toString()  : '',
          tva_20:      n.tva_20  ? n.tva_20.toString()  : '',
          project_id:  n.project_id || '',
          commentaire: n.commentaire || '',
        });
        if (n.justificatif_name) {
          notesFraisAPI.getJustificatif(id)
            .then(r => setPreview({ url: r.data.url, type: r.data.mime || '', name: r.data.name }))
            .catch(() => setPreview({ url: null, type: '', name: n.justificatif_name, unavailable: true }));
        }
      })
      .catch(() => toast.error('Erreur chargement note'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const onFileChange = (f) => {
    if (!f) return;
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview({ url, type: f.type, name: f.name });
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('poste', form.poste);
    fd.append('designation', form.designation);
    fd.append('date_effet', form.date_effet);
    fd.append('montant_ttc', form.montant_ttc || 0);
    fd.append('tva_5_5', form.tva_5_5 || 0);
    fd.append('tva_10', form.tva_10 || 0);
    fd.append('tva_20', form.tva_20 || 0);
    if (form.project_id) fd.append('project_id', form.project_id);
    if (form.commentaire) fd.append('commentaire', form.commentaire);
    if (file) fd.append('justificatif', file);
    return fd;
  };

  const validate = () => {
    if (!form.designation.trim()) { toast.error('La désignation est obligatoire'); return false; }
    if (!form.date_effet)          { toast.error('La date d\'effet est obligatoire'); return false; }
    if (!form.montant_ttc || parseFloat(form.montant_ttc) <= 0) {
      toast.error('Le montant TTC doit être supérieur à 0'); return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const fd = buildFormData();
      if (isEdit) {
        await notesFraisAPI.update(id, fd);
      } else {
        await notesFraisAPI.create(fd);
      }
      toast.success('Note enregistrée');
      navigate('/notes-frais');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const fd = buildFormData();
      let res;
      if (isEdit) {
        res = await notesFraisAPI.update(id, fd);
      } else {
        res = await notesFraisAPI.create(fd);
      }
      const nfId = res.data.id;
      await notesFraisAPI.submit(nfId);
      toast.success('Note soumise pour validation');
      navigate('/notes-frais');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur soumission');
    } finally {
      setSaving(false);
    }
  };

  const canEdit = !note || ['BROUILLON', 'REFUSE'].includes(note.statut);
  const sm = note ? (STATUT_META[note.statut] || STATUT_META.BROUILLON) : null;

  if (loading) return <div className="loading-state"><div className="spinner"/></div>;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/notes-frais')}>
              <ChevronLeft size={16}/>
            </button>
            <div>
              <h1 style={{ margin: 0 }}>
                {isEdit ? `Note de frais #${note?.numero || '…'}` : 'Nouveau frais'}
              </h1>
              <p style={{ margin: 0 }}>
                {isEdit ? note?.designation : 'Saisir un nouveau justificatif de dépense'}
              </p>
            </div>
          </div>
        </div>
        {sm && (
          <div className="page-header-actions">
            <span className={`nf-badge ${sm.cls}`} style={{ fontSize: '0.825rem', padding: '5px 14px' }}>
              {sm.label}
            </span>
          </div>
        )}
      </div>

      <div className="card">
        <div className="nf-form-layout">
          {/* Left — justificatif */}
          <div>
            <label className="form-label" style={{ display: 'block', marginBottom: 8 }}>
              Justificatif (PDF ou image)
            </label>

            {preview ? (
              <div className="nf-preview">
                {preview.unavailable ? (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', gap: 8, padding: '24px 16px',
                    color: 'var(--text-muted)', fontSize: '0.8rem', textAlign: 'center',
                  }}>
                    <FileText size={36} style={{ opacity: 0.4 }}/>
                    <span><strong>{preview.name}</strong></span>
                    <span style={{ color: 'var(--text-muted)' }}>
                      Fichier non disponible suite à une migration.<br/>
                      {canEdit ? 'Veuillez re-uploader le justificatif.' : 'Contactez l\'administrateur.'}
                    </span>
                  </div>
                ) : preview.type?.startsWith('image/') ? (
                  <img src={preview.url} alt={preview.name}/>
                ) : (
                  <iframe src={preview.url} title={preview.name}/>
                )}
                <div className="nf-preview-name">{preview.name}</div>
                {canEdit && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => { setFile(null); setPreview(null); }}
                    style={{ alignSelf: 'center' }}
                  >
                    <X size={13}/> Changer le fichier
                  </button>
                )}
              </div>
            ) : (
              <div
                className={`nf-upload-zone${drag ? ' nf-upload-zone--drag' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault(); setDrag(false);
                  const f = e.dataTransfer.files[0];
                  if (f) onFileChange(f);
                }}
                onClick={() => fileRef.current?.click()}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => onFileChange(e.target.files[0])}
                  onClick={(e) => e.stopPropagation()}
                />
                <Upload size={36} className="nf-upload-icon"/>
                <div className="nf-upload-hint">
                  <strong>Cliquez</strong> ou glissez un fichier ici<br/>
                  PDF, PNG, JPG — max 10 Mo
                </div>
              </div>
            )}
          </div>

          {/* Right — fields */}
          <div className="nf-form-fields">
            <div className="form-group">
              <label className="form-label">Poste de dépense *</label>
              <select className="form-control" value={form.poste} onChange={set('poste')} disabled={!canEdit}>
                {POSTES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Désignation *</label>
              <input
                className="form-control"
                value={form.designation}
                onChange={set('designation')}
                placeholder="Ex: Taxi aéroport — mission client Paris"
                disabled={!canEdit}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Date d'effet *</label>
              <input
                type="date"
                className="form-control"
                value={form.date_effet}
                onChange={set('date_effet')}
                disabled={!canEdit}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Montant TTC (€) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                value={form.montant_ttc}
                onChange={set('montant_ttc')}
                placeholder="0.00"
                disabled={!canEdit}
              />
            </div>

            <div className="form-group">
              <label className="form-label">TVA (€)</label>
              <div className="nf-tva-row">
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>5.5%</label>
                  <input type="number" step="0.01" min="0" className="form-control" value={form.tva_5_5}
                    onChange={set('tva_5_5')} placeholder="0.00" disabled={!canEdit}/>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>10%</label>
                  <input type="number" step="0.01" min="0" className="form-control" value={form.tva_10}
                    onChange={set('tva_10')} placeholder="0.00" disabled={!canEdit}/>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>20%</label>
                  <input type="number" step="0.01" min="0" className="form-control" value={form.tva_20}
                    onChange={set('tva_20')} placeholder="0.00" disabled={!canEdit}/>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Projet</label>
              <select className="form-control" value={form.project_id} onChange={set('project_id')} disabled={!canEdit}>
                <option value="">— Aucun projet —</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.nom}{p.client ? ` — ${p.client}` : ''}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Commentaire</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.commentaire}
                onChange={set('commentaire')}
                placeholder="Contexte, précisions…"
                style={{ resize: 'vertical' }}
                disabled={!canEdit}
              />
            </div>

            {note?.commentaire_valideur && (
              <div style={{
                background: 'rgba(220,38,38,0.06)',
                border: '1px solid rgba(220,38,38,0.25)',
                borderRadius: 10,
                padding: '10px 14px',
                fontSize: '0.8rem',
                color: '#DC2626',
              }}>
                <strong>Commentaire valideur :</strong> {note.commentaire_valideur}
              </div>
            )}

            {canEdit && (
              <div className="nf-form-actions">
                <button className="btn btn-secondary" onClick={() => navigate('/notes-frais')} disabled={saving}>
                  Annuler
                </button>
                <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button className="btn btn-primary" onClick={handleSaveAndSubmit} disabled={saving}>
                  {saving ? '…' : 'Enregistrer et soumettre'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
